import React, { useState, useEffect, useCallback } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  Search, Users, Receipt, School, 
  LayoutDashboard, UserPlus, FileText, CheckCircle2, ChevronRight 
} from 'lucide-react';

// Premium Glassmorphism CMD Styles
const SPOTLIGHT_CSS = `
  [cmdk-overlay] {
    position: fixed;
    inset: 0;
    z-index: 9998;
    background: rgba(15,23,42,0.3);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    animation: sp-fade-in 0.2s ease;
  }

  [cmdk-dialog] {
    position: fixed;
    top: 15vh;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 640px;
    z-index: 9999;
    animation: sp-slide-down 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  [cmdk-root] {
    width: 100%;
    background: linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.85) 100%);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border-radius: 24px;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.6), 0 40px 80px -20px rgba(99,102,241,0.25);
    overflow: hidden;
    color: #1e293b;
   
  }

  [cmdk-input-wrapper] {
    display: flex;
    align-items: center;
    padding: 18px 24px;
    border-bottom: 1px solid rgba(226,232,240,0.6);
  }

  [cmdk-input] {
    border: none;
    background: transparent;
    width: 100%;
    font-size: 20px;
    font-weight: 500;
    color: #0f172a;
    outline: none;
    margin-left: 12px;
    caret-color: #6366f1;
  }
  
  [cmdk-input]::placeholder {
    color: #94a3b8;
  }

  [cmdk-list] {
    max-height: 400px;
    overflow-y: auto;
    padding: 12px;
    scroll-behavior: smooth;
  }

  [cmdk-group-heading] {
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #64748b;
  }

  [cmdk-item] {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 4px;
  }

  [cmdk-item][data-selected="true"] {
    background: rgba(99,102,241,0.08);
  }

  [cmdk-item]:active {
    transform: scale(0.98);
  }

  .sp-icon-box {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .sp-content {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .sp-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .sp-subtitle {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
  }

  .sp-empty {
    padding: 40px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
  }

  @keyframes sp-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes sp-slide-down {
    from { opacity: 0; transform: translate(-50%, -20px) scale(0.96); }
    to { opacity: 1; transform: translate(-50%, 0) scale(1); }
  }
`;

// Helper icons by category
const getIconForType = (type) => {
  switch(type) {
    case 'student': return <Users size={18} color="#6366f1" />;
    case 'class': return <School size={18} color="#8b5cf6" />;
    case 'receipt': return <Receipt size={18} color="#f59e0b" />;
    case 'action': return <CheckCircle2 size={18} color="#10b981" />;
    case 'page': return <LayoutDashboard size={18} color="#3b82f6" />;
    default: return <FileText size={18} color="#64748b" />;
  }
};

const getColorForType = (type) => {
  switch(type) {
    case 'student': return 'rgba(99,102,241,0.1)';
    case 'class': return 'rgba(139,92,246,0.1)';
    case 'receipt': return 'rgba(245,158,11,0.1)';
    case 'action': return 'rgba(16,185,129,0.1)';
    case 'page': return 'rgba(59,130,246,0.1)';
    default: return 'rgba(100,116,139,0.1)';
  }
};

const QUICK_ACTIONS = [
  { id: 'add-student', type: 'action', title: 'Add New Student', subtitle: 'Quick Action', path: '/admin/students' },
  { id: 'collect-fee', type: 'action', title: 'Collect Fee', subtitle: 'Quick Action', path: '/admin/fees' },
  { id: 'add-class', type: 'action', title: 'Create Class', subtitle: 'Quick Action', path: '/admin/classes' },
];

const PAGES = [
  { id: 'p-dash', type: 'page', title: 'Dashboard', subtitle: 'System Core', path: '/dashboard' },
  { id: 'p-fees', type: 'page', title: 'Fees Ledger', subtitle: 'Finance', path: '/admin/fees' },
  { id: 'p-stu', type: 'page', title: 'Students', subtitle: 'Directory', path: '/admin/students' },
  { id: 'p-set', type: 'page', title: 'Settings', subtitle: 'Configuration', path: '/admin/settings' },
];

export function SpotlightSearch({ open, setOpen }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Keyboard shortcut listener
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [setOpen]);

  // Debounced Search API
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(query)}`);
        setResults(res.data || []);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 250); // 250ms debounce

    return () => clearTimeout(timer);
  }, [query]);

  const onSelect = (item) => {
    setOpen(false);
    setQuery('');
    if (item.path) {
      navigate(item.path);
    }
  };

  // Grouping logic for rendering
  const groupedResults = results.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  const renderItem = (item) => (
    <Command.Item key={item.id} value={item.title} onSelect={() => onSelect(item)}>
      <div className="sp-icon-box" style={{ background: getColorForType(item.type) }}>
        {getIconForType(item.type)}
      </div>
      <div className="sp-content">
        <span className="sp-title">{item.title}</span>
        <span className="sp-subtitle">{item.subtitle}</span>
      </div>
      <ChevronRight size={16} color="#cbd5e1" />
    </Command.Item>
  );

  return (
    <>
      <style>{SPOTLIGHT_CSS}</style>
      <Command.Dialog 
        open={open} 
        onOpenChange={setOpen} 
        label="Global Spotlight Search"
        shouldFilter={false} // We do filtering via API, except for empty state
      >
        <div cmdk-input-wrapper="">
          <Search size={24} color="#6366f1" />
          <Command.Input 
            autoFocus 
            placeholder="Search students, receipts, classes, or jump to..." 
            value={query} 
            onValueChange={setQuery} 
          />
          {loading && (
            <div style={{ width: '16px', height: '16px', border: '2px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          )}
        </div>

        <Command.List>
          {/* Default Empty State */}
          {!query && (
            <>
              <Command.Group heading="Quick Actions">
                {QUICK_ACTIONS.map(renderItem)}
              </Command.Group>
              <Command.Group heading="Navigation">
                {PAGES.map(renderItem)}
              </Command.Group>
            </>
          )}

          {/* Search Results */}
          {query && Object.keys(groupedResults).map((type) => (
            <Command.Group key={type} heading={type + 's'}>
              {groupedResults[type].map(renderItem)}
            </Command.Group>
          ))}

          {query && results.length === 0 && !loading && (
            <div className="sp-empty">
              No results found for "{query}". Try checking for typos.
            </div>
          )}
        </Command.List>
      </Command.Dialog>
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
